import type { SynergyFigure } from '../../synergy-risk';
import type { AskViewAddress } from './view-payload';
import type { DrillAnnotation, DrillOutcome, DrillTarget } from './drill-read';

/** Figures are computed and formatted by the server; clients display strings verbatim. */
export type AskViewDriverFigure = SynergyFigure;
/** Adapter cases are structural, separate from the derive engine's original refusal code. */
export const ASK_DRIVER_SHARE_ADAPTER_CODES = [
  'missing_run', 'unanswered_result', 'missing_value', 'multiple_values', 'missing_operands', 'derive_not_composed',
] as const;
export type AskViewDriverShareAdapterCode = typeof ASK_DRIVER_SHARE_ADAPTER_CODES[number];
export type AskViewDriverShareUnavailable =
  | Readonly<{ kind: 'derive_refusal'; code: string; reason: string }>
  | Readonly<{ kind: 'adapter_unavailable'; code: AskViewDriverShareAdapterCode; reason: string }>;
export const ASK_DRIVER_MAPPING_UNAVAILABLE_CODES = [
  'mapping_gap', 'mapping_not_effective', 'target_unavailable',
] as const;
export type AskViewDriverMappingUnavailable = Readonly<{
  kind: 'not_available';
  code: typeof ASK_DRIVER_MAPPING_UNAVAILABLE_CODES[number];
  reason: string;
}>;
export type AskViewDriverNode = Readonly<{
  key: string;
  label: string | null;
  childrenKeys: readonly string[];
  value: AskViewDriverFigure | null;
  share: AskViewDriverFigure | null;
  /** Additive outcome for a missing share; independently valid values stay visible. */
  shareUnavailable?: AskViewDriverShareUnavailable | null;
  /** Mapping availability is independent of governed deal values and derived shares. */
  mappingUnavailable?: AskViewDriverMappingUnavailable;
  /** Engine-produced remainder beside immediate children, never a balancing UI calculation. */
  residual: AskViewDriverFigure | null;
  flagged: boolean;
  note: string | null;
  /** Server-resolved next selection; expanding a row never follows this address. */
  drillTarget?: DrillTarget;
  /** Published children are present in this payload; detail opens them in place. */
  detailsInline?: boolean;
}>;
export type AskViewItTakeout = Readonly<{
  rows: readonly Readonly<{ label: string; committed: AskViewDriverFigure | null;
    it: AskViewDriverFigure | null; address: AskViewAddress }>[];
  note: string;
}>;
export type AskViewDriverTree = Readonly<{
  title: string;
  address: AskViewAddress;
  rootKey: string;
  nodes: readonly AskViewDriverNode[];
  outcome?: DrillOutcome;
  annotations?: readonly DrillAnnotation[];
  sourceLabel?: string;
  /** Server-authored measure heading and separation of planning allocations from commitments. */
  valueLabel?: string;
  allocation?: Readonly<{ title: string; note: string }>;
  aggregation?: 'individual_values';
  nextPage?: DrillTarget;
}>;

/** An optional tree may be unavailable while every other commitment panel remains valid. */
export type AskViewDriverTreeUnavailable = Readonly<{
  kind: 'not_yet';
  /** The original tree contract error code, never a replacement answer or invented value. */
  code: string;
  nodeKey: string | null;
  reason: string;
}>;
